import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import httpService from './httpService'
import { MyBenefitQueryKeys, ResolvedTerm } from './myBenefits'
import type { EntityKindSchema } from '@/app/commons/generated'
import { Response } from '@/types'

/**
 * The coach's own side of benefits — who has claimed with me, and the profile members read.
 *
 * Not what I offer: that is authored centrally by DUPR operations through the admin panel, so
 * nothing here writes an offer row.
 *
 * Addressed by KIND and id throughout, never by id alone, because that is how the entity platform
 * addresses anything: `independent-coach` and `club-coach` are separate kinds that can each hold
 * id 7. Every hook here takes the pair for the same reason the API does.
 *
 * Kind-agnostic beyond that. Nothing below knows what coaching is — the accept action is
 * `steps/{stepKey}` with the key the server named as outstanding, so a claim flow that grows a
 * second step needs no change here.
 */

/** What an entity has authored about itself. Shapes vary by kind; the discriminator is `kind`. */
export interface EntityAttributes {
  kind?: string
  headline?: string | null
  bio?: string | null
  yearsCoaching?: number | null
  certifications?: string[] | null
  website?: string | null
  acceptingStudents?: boolean | null
  /**
   * The shape is the server's sealed type, and it grows without asking this client.
   *
   * The index signature is not laziness: a save replaces the whole attribute object, so any field
   * this file has not heard of would be dropped on the first edit. Carrying the unknown ones
   * through is what makes an editor that knows four fields safe on a shape that has six.
   */
  [key: string]: unknown
}

export interface EntityRelationView {
  relation: string
  subjectUrn: string
}

/** An entity as its operator sees it — everything, relations included. */
export interface OperatedEntity {
  urn: string
  id: number
  kind: string
  kindLabel: string
  /** The status KEY, lower-case — `draft`, `active`, `suspended`. Not the label. */
  status: string
  attributes: EntityAttributes | null
  relations: EntityRelationView[]
  created: string
}

/**
 * One member's claim, as the target sees it.
 *
 * `pendingStep` is the difference between a club's list and a coach's queue: a club's claims
 * complete on assignment and it is always null, while a coach's list is a to-do. The button is
 * driven from this value rather than from a hard-coded "accept", so the server stays the authority
 * on what is outstanding.
 */
export interface CoachClaim {
  grantId: number
  name: string
  /**
   * The claimant's obfuscated player id, so a target can open the profile of whoever booked them.
   *
   * Given on the same terms as {@link duprId}, and for the same reason: this list exists to
   * identify the person turning up, and both answer that question. Optional because a client may be
   * talking to a server from before the field existed — one that cannot tell what it holds should
   * show the name and link nowhere.
   */
  playerId?: number
  /** The member's DUPR id, for matching a person standing in front of you. */
  duprId: string | null
  benefitUrn: string
  label: string | null
  terms: ResolvedTerm[]
  /** The server's sentence for what they are owed; show verbatim. Absent from older servers. */
  worth?: string
  claimedAt: string
  redeemedAt: string | null
  pendingStep: string | null
  dueAt: string | null
}

export const CoachQueryKeys = {
  mine: ['operated-entities'] as const,
  kinds: ['entity-kinds'] as const,
  claims: (kind: string | null, id: number | null) => ['coach-claims', kind, id] as const,
}

type ResultsOf<T> = Response & { results: T[] }

const base = (kind: string, id: number) => `/entities/${kind}/${id}/benefits/v1.0`

/**
 * The entities this member operates.
 *
 * Empty for almost everybody — most members coach nothing — which is exactly what the coaching
 * surface keys off to decide whether it has anything to show.
 */
export const useMyEntities = (enabled = true) =>
  useQuery({
    queryKey: CoachQueryKeys.mine,
    enabled,
    queryFn: async ({ signal }) => {
      const res = await httpService.Get<ResultsOf<OperatedEntity>>('/entities/me', { signal })
      return res.results
    },
  })

/** Who has claimed a benefit here, newest first. */
export const useCoachClaims = (kind: string | null, entityId: number | null) =>
  useQuery({
    queryKey: CoachQueryKeys.claims(kind, entityId),
    enabled: kind !== null && entityId !== null,
    queryFn: async ({ signal }) => {
      if (kind === null || entityId === null) throw new Error('Cannot list claims before the entity is known')
      const res = await httpService.Get<ResultsOf<CoachClaim>>(`${base(kind, entityId)}/claims`, { signal })
      return res.results
    },
  })

/**
 * Answering a claim: completing its outstanding step, refusing it, or marking it honored.
 *
 * One mutation for all three because every one of them returns the same thing — the entity's whole
 * claim list, freshly read from the writer — so they share their success handling exactly. Split
 * into three hooks, each would have had its own copy of the cache updates, and the copies drift.
 */
export const useAnswerClaim = (kind: string | null, entityId: number | null) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      grantId,
      action,
      reason,
    }: {
      grantId: number
      /** `{ step: key }` completes whatever the queue reported as outstanding. */
      action: { step: string } | { decline: true } | { redeem: true }
      reason?: string
    }) => {
      if (kind === null || entityId === null) throw new Error('Cannot answer a claim before the entity is known')
      const path = `${base(kind, entityId)}/${grantId}`
      if ('step' in action) {
        return httpService.Post<ResultsOf<CoachClaim>>(`${path}/steps/${action.step}`, {})
      }
      if ('decline' in action) {
        return httpService.Post<ResultsOf<CoachClaim>>(`${path}/decline`, reason ? { reason } : {})
      }
      return httpService.Post<ResultsOf<CoachClaim>>(`${path}/redeem`, {})
    },
    onSuccess: (res) => {
      // The response IS the new list, read from the writer precisely so this does not have to
      // round-trip a replica that has not caught up yet.
      queryClient.setQueryData(CoachQueryKeys.claims(kind, entityId), res.results)
      // The member's own view of the same claim has changed too — accepting moves it out of
      // "waiting to be accepted", and declining hands the benefit back to them.
      queryClient.invalidateQueries({ queryKey: MyBenefitQueryKeys.mine })
    },
  })
}

/** Edit an entity's own profile — the headline and bio members read before choosing. */
export const useSaveEntityAttributes = (kind: string | null, entityId: number | null) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (attributes: EntityAttributes | Record<string, unknown>) => {
      if (kind === null || entityId === null) throw new Error('Cannot save a profile before the entity is known')
      // The discriminator is the entity's own kind; the server rejects attributes shaped for a
      // different one, so it is set here rather than left to the caller to remember.
      return httpService.Put<ResultsOf<OperatedEntity>>(`/entities/${kind}/${entityId}/attributes`, {
        attributes: { ...attributes, kind },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CoachQueryKeys.mine })
      queryClient.invalidateQueries({ queryKey: MyBenefitQueryKeys.coachProfile(kind, entityId) })
    },
  })
}

/**
 * Every entity kind and the shape it accepts.
 *
 * The `attributes` on each kind is a `SealedTypeSchema` — the server's reflection over the sealed
 * attribute type — which is what lets a profile form and a profile page render themselves. Cached
 * indefinitely by React Query's defaults for the session: it changes only when the server deploys.
 */
export const useEntityKinds = (enabled = true) =>
  useQuery({
    queryKey: CoachQueryKeys.kinds,
    enabled,
    queryFn: async ({ signal }) => {
      const res = await httpService.Get<ResultsOf<EntityKindSchema>>('/entities/kinds', { signal })
      return res.results
    },
  })
